import { DirectionalLight, ShadowLight } from '@babylonjs/core/Lights';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { eShadowFilter, eShadowResolution } from '@kb3d/models/classes/enums';
import { getByDynamicId } from '@kb3d/models/classes/kb3d-manager';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { BoundingBoxCache, BoundingBoxInfo } from '@kb3d/view/modules/bounding-box-cache';
import { Subscription } from 'rxjs';

/**
 * Manages shadow generators and their association with lights and meshes that participate
 */
export class ShadowManager {
    constructor(private _bbCache: BoundingBoxCache) {
        this._bbCache.rootBoundingBoxChanged.subscribe(bbInfo => {
            //invalidate our projection matrix cache
            this.projectionMatrixCache.clear();
        });
    }

    private generatorMap = new Map<ShadowLight, ShadowGenerator>();
    /** the meshes that are casting shadows.  The mesh renderer adds and removes them from this list as their boolean flag is changed */
    private casters = new Set<AbstractMesh>();
    /** the meshes that are receiving shadows.  The mesh renderer adds and removes them from this list as their boolean flag is changed */
    private receivers = new Set<AbstractMesh>();
    /**
     * Stores the calculated shadow projection matrix for directional lights.  This cache is invalidated when
     * the root scene bounding box has changed or meshes change their casting / receiving flags.
     *
     * By default, babylon calculates the projection matrix of the shadows on every frame.
     * To be more efficient, we cache the projection matrix until something that warrants updating it happens.
     */
    private projectionMatrixCache = new Map<DirectionalLight, Matrix>();

    private bbSubscription: Subscription;
    /**
     * Add a shadow generator for the given light.  Should only be called when the light.generatesShadows is set to true
     * @param light
     */
    public add(light: ShadowLight, shadowResolution: eShadowResolution) {
        let sg = this.get(light);
        if (!sg) {
            sg = new ShadowGenerator(ShadowManager.convertShadowResultion(shadowResolution), light);
            this.generatorMap.set(light, sg);
            this.addAllCastersToGenerator(sg);
            if (light instanceof DirectionalLight) {
                /**
                 * Directional lights are tricky, as their light shadow fulstrum needs to be the optimal size for the shadows to look good.
                 * The built-in calculation of the projection matrix does not account for meshes that are receiving shadows.  So we override
                 * the calculation here.  It also gives us a way to cache and increase performance.
                 */
                light.autoUpdateExtends = false;
                light.autoCalcShadowZBounds = false;
                light.customProjectionMatrixBuilder = (
                    viewMatrix: Matrix,
                    renderList: AbstractMesh[],
                    matrix: Matrix
                ) => {
                    return this.customProjectionMatrixBuilder(light, viewMatrix, renderList, matrix);
                };
            }
        }
        return sg;
    }

    /**
     * Get the shadow generator for a specific light (if it has one)
     * @param light
     */
    public get(light: ShadowLight) {
        return this.generatorMap.get(light);
    }

    /** Remove the shadow generator for the given light */
    public remove(light: ShadowLight) {
        const sg = this.get(light);
        (light as any).customProjectionMatrixBuilder = undefined;
        if (sg) {
            this.generatorMap.delete(light);
            sg.dispose();
        }
    }

    /**
     * Adds a mesh as a shadow caster.  Should only be called by the mesh renderer when the meshNode.castShadows flag is changed to true
     */
    public addCaster(mesh: AbstractMesh) {
        if (!this.casters.has(mesh)) {
            this.casters.add(mesh);
            for (const sg of this.generatorMap.values()) {
                sg.addShadowCaster(mesh);
            }
            this.projectionMatrixCache.clear();
        }
    }

    /**
     * Removes a mesh as a shadow caster.  Should only be called by the mesh renderer when the meshNode.castShadows flag is changed to false
     */
    public removeCaster(mesh: AbstractMesh) {
        if (this.casters.has(mesh)) {
            this.casters.delete(mesh);
            for (const sg of this.generatorMap.values()) {
                sg.removeShadowCaster(mesh);
            }
            this.projectionMatrixCache.clear();
        }
    }

    /**
     * Adds/removes the mesh as a shadow caster
     * @param mesh
     * @param value
     */
    public toggleCaster(mesh: Mesh, value: boolean) {
        if (value) {
            this.addCaster(mesh);
        } else {
            this.removeCaster(mesh);
        }
    }

    protected addAllCastersToGenerator(sg: ShadowGenerator) {
        for (const c of this.casters.values()) {
            sg.addShadowCaster(c);
        }
    }

    public addReceiver(mesh: AbstractMesh) {
        if (!this.receivers.has(mesh)) {
            this.receivers.add(mesh);
            this.projectionMatrixCache.clear();
        }
    }

    public removeReceiver(mesh: AbstractMesh) {
        if (this.receivers.has(mesh)) {
            this.receivers.delete(mesh);
            this.projectionMatrixCache.clear();
        }
    }

    public toggleReceiver(mesh: AbstractMesh, value: boolean) {
        if (value) {
            this.addReceiver(mesh);
        } else {
            this.removeReceiver(mesh);
        }
    }

    public dispose() {
        this.projectionMatrixCache.clear();
        for (const light of this.generatorMap.keys()) {
            this.remove(light);
        }
    }

    public clearProjectMatrixCache() {
        this.projectionMatrixCache.clear();
    }
    /**
     * Directional lights are tricky, as their light shadow fulstrum needs to be the optimal size for the shadows to look good.
     * The built-in calculation of the projection matrix does not account for meshes that are receiving shadows.  So we override
     * the calculation here.  It also gives us a way to cache and increase performance.
     *
     * This is based on the built-in DirectionalLight.prototype._setDefaultAutoExtendShadowProjectionMatrix
     * but with these additions:
     * * caching of the projection matrix
     * * inclusion of shadow receivers in the box calculation so shadows are not chopped off with PCSS
     * * uses the shadowOrthoScale of the light to increase the minZ/maxZ range to avoid z-fighting on planes at one of the extents of the box
     */
    protected customProjectionMatrixBuilder(
        light: DirectionalLight,
        viewMatrix: Matrix,
        renderList: AbstractMesh[],
        matrix: Matrix
    ) {
        if (this.projectionMatrixCache.has(light)) {
            matrix.copyFrom(this.projectionMatrixCache.get(light)!);
        } else {
            const allMeshes = new Set([...this.casters, ...this.receivers]);

            const shadowOrthoScale = light.shadowOrthoScale;
            let orthoLeft = Number.MAX_VALUE;
            let orthoRight = Number.MIN_VALUE;
            let orthoTop = Number.MIN_VALUE;
            let orthoBottom = Number.MAX_VALUE;
            let shadowMinZ = Number.MAX_VALUE;
            let shadowMaxZ = Number.MIN_VALUE;
            const tempVector3 = Vector3.Zero();

            //loop through each caster/reciever to build up the fulstrum box
            for (const m of allMeshes) {
                if (m.isEnabled(true)) {
                    const node = getByDynamicId<SpaceNode>(m.id);
                    let bbInfo: BoundingBoxInfo | undefined;
                    if (node) {
                        bbInfo = this._bbCache.get(node);
                    } else {
                        // //not a kb3d node, but could be a shadow plan in XR
                        // let binfo = m.getBoundingInfo();
                        // if (binfo){
                        //     bbInfo = toBoundingBoxInfo(binfo);
                        // }
                    }
                    if (bbInfo) {
                        const boundingBox = bbInfo.boundingInfo.boundingBox;
                        for (let index = 0; index < boundingBox.vectorsWorld.length; index++) {
                            Vector3.TransformCoordinatesToRef(boundingBox.vectorsWorld[index], viewMatrix, tempVector3);
                            if (tempVector3.x < orthoLeft) {
                                orthoLeft = tempVector3.x;
                            }
                            if (tempVector3.y < orthoBottom) {
                                orthoBottom = tempVector3.y;
                            }
                            if (tempVector3.x > orthoRight) {
                                orthoRight = tempVector3.x;
                            }
                            if (tempVector3.y > orthoTop) {
                                orthoTop = tempVector3.y;
                            }

                            if (tempVector3.z < shadowMinZ) {
                                shadowMinZ = tempVector3.z;
                            }
                            if (tempVector3.z > shadowMaxZ) {
                                shadowMaxZ = tempVector3.z;
                            }
                        }
                    }
                }
            }

            const xOffset = orthoRight - orthoLeft;
            const yOffset = orthoTop - orthoBottom;

            /** for exponential blur, it works way better when the max z is alot larger than the scene to get soft shadows */
            const sg = this.get(light)!;
            if (sg.filter == ShadowGenerator.FILTER_BLUREXPONENTIALSHADOWMAP) {
                shadowMaxZ = shadowMaxZ * 50;
            }
            const zOffset = shadowMaxZ - shadowMinZ;

            Matrix.OrthoOffCenterLHToRef(
                orthoLeft - xOffset * shadowOrthoScale,
                orthoRight + xOffset * shadowOrthoScale,
                orthoBottom - yOffset * shadowOrthoScale,
                orthoTop + yOffset * shadowOrthoScale,
                /**
                 * Babylon does not account for the orthoScale when calculating minZ/maxZ, which leads to shadow z fighting on planes receiving shadows.
                 * So here we change this behavior to offset by the scale just like left/right/top/bottom
                 */
                shadowMinZ - zOffset * shadowOrthoScale,
                shadowMaxZ + zOffset * shadowOrthoScale,
                matrix
            );

            this.projectionMatrixCache.set(light, matrix);
            // console.count("shadow manager processed");
        }
    }

    public static convertShadowResultion(r: eShadowResolution) {
        switch (r) {
            case eShadowResolution.low:
                return 512;
            case eShadowResolution.medium:
                return 1024;
            case eShadowResolution.high:
                return 2048;
        }
    }

    public static convertShadowFilter(f: eShadowFilter) {
        switch (f) {
            case eShadowFilter.none:
                return ShadowGenerator.FILTER_NONE;
            case eShadowFilter.simple:
                return ShadowGenerator.FILTER_PCF;
            case eShadowFilter.advanced:
                return ShadowGenerator.FILTER_PCSS;
        }
    }
}
